import * as Sentry from '@sentry/react';
import variables from '@workspace/api/variables';

const ignoreErrors = [
  // Erreurs réseau non critiques
  'Network Error',
  'NetworkError',
  'Failed to fetch',
  'Load failed',
  'Loading chunk',
  'ChunkLoadError',

  // Erreurs HTTP côté client (4xx)
  // /4[0-9]{2}/, // Toutes les erreurs 4xx
  'Request failed with status code 422',
  // 'Request failed with status code 400',
  'Request failed with status code 401',
  'Request failed with status code 403',
  // 'Request failed with status code 404',

  // Erreurs de navigation/browser
  'Navigation cancelled',
  'Non-Error promise rejection captured',
  'Cancelled',
  'AbortError',

  // Script loading (CDN, extensions browser)
  'Script error',
  'Script error.',
  /extension\//i,
  /^moz-extension:\/\//,
  /^chrome-extension:\/\//,

  // Erreurs UI non critiques
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  /Blocked a frame with origin/,
];

const SENSITIVE_FIELDS = [
  'IBAN', 'NomTitulaire', 'BIC',
  'Email', 'ConfirmEmail', 'SignatureEmail',
  'Nom', 'Prenom',
  'Telephone', 'TelephonePortable', 'SignaturePhone', 'PhoneContactConsent',
  'Adresse', 'CodePostal', 'Ville',
  'DateNaissance',
  'password', 'token', 'access_token', 'auth_token',
];

const SENSITIVE_PATTERN = new RegExp(SENSITIVE_FIELDS.join('|'), 'i');

function scrubSensitiveData(data: Record<string, unknown>): Record<string, unknown> {
  const scrubbed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (SENSITIVE_PATTERN.test(key)) {
      scrubbed[key] = '[Filtered]';
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      scrubbed[key] = scrubSensitiveData(value as Record<string, unknown>);
    } else {
      scrubbed[key] = value;
    }
  }
  return scrubbed;
}

export const sentryInit = () => {
  const env = (import.meta as unknown as { env: Record<string, string> }).env;
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN || '',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        mask: [
          'input[type="password"]',
          'input[name*="IBAN"]',
          'input[name*="NomTitulaire"]',
          'input[name*="BIC"]',
          'input[name*="Email"]',
          'input[name*="Nom"]',
          'input[name*="Prenom"]',
          'input[name*="Telephone"]',
          'input[name*="Phone"]',
          'input[name*="DateNaissance"]',
          'input[name*="Adresse"]',
          'input[name*="CodePostal"]',
          'input[name*="Ville"]',
        ],
        block: [
          '.sensitive-media',
        ],
      }),
    ],
    ignoreErrors,
    beforeSend(event) {
      if (event.request?.data && typeof event.request.data === 'object') {
        event.request.data = scrubSensitiveData(event.request.data as Record<string, unknown>);
      }
      if (event.contexts) {
        event.contexts = scrubSensitiveData(event.contexts as Record<string, unknown>) as typeof event.contexts;
      }
      if (event.extra) {
        event.extra = scrubSensitiveData(event.extra as Record<string, unknown>);
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
          if (breadcrumb.data && typeof breadcrumb.data === 'object') {
            breadcrumb.data = scrubSensitiveData(breadcrumb.data as Record<string, unknown>);
          }
          return breadcrumb;
        });
      }
      return event;
    },
    // Performance Monitoring
    tracesSampleRate: 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 0.1,
    tracePropagationTargets:[
      variables.WS_URL,
      variables.SERVICES_URL,
      variables.WHEELS_URL,
      variables.PASS_URL,
      variables.CALENDAR_URL,
      variables.DOCUMENTS_URL,
    ]
  });
};
